import { useMemo } from 'react'
import { ApexOptions } from "apexcharts"
import ReactApexChart from "react-apexcharts"
import moment from 'moment'

interface ITimelineData {
  separated_hour: string
  total_items: number
  total_secs: number
  avg_secs: number
  users: any[]
}

interface IHourlyAverageChartProps {
  timelineData: ITimelineData[]
  selectedSeparator?: string
  title?: string
  onBarClick?: (hourData: {
    hour: string
    users: any[]
    avg_secs: number
    total_items: number
    total_secs: number
  }) => void
}

const HourlyAverageChart = ({
  timelineData,
  selectedSeparator,
  title = "Média Global por Horário",
  onBarClick
}: IHourlyAverageChartProps) => {
  const { hourlyData, globalAverage } = useMemo(() => {
    if (!timelineData || timelineData.length === 0) {
      return { hourlyData: [], globalAverage: 0 }
    }

    // Processar dados por horário
    const hourlyData = timelineData.map(entry => {
      let avgSeconds, totalItems, totalSecs, activeUsers

      if (selectedSeparator) {
        // Filtrar por separador específico
        const separatorData = entry.users.find((user: any) => user.username === selectedSeparator)
        avgSeconds = separatorData?.avg_secs || 0
        totalItems = separatorData?.total_items || 0
        totalSecs = separatorData?.total_secs || 0
        activeUsers = separatorData ? 1 : 0
      } else {
        // Usar dados globais
        avgSeconds = entry.avg_secs
        totalItems = entry.total_items
        totalSecs = entry.total_secs
        activeUsers = entry.users.length
      }

      return {
        hour: moment(entry.separated_hour).format('HH:mm'),
        hourNumber: moment(entry.separated_hour).hour(),
        avgSeconds,
        totalItems,
        totalSecs,
        activeUsers,
        // Adicionar ordem customizada para turno noturno (madrugada no final)
        sortOrder: moment(entry.separated_hour).hour() <= 4 ?
          moment(entry.separated_hour).hour() + 24 : // 00h-04h vão para 24h-28h
          moment(entry.separated_hour).hour()
      }
    }).filter(data => data.avgSeconds > 0).sort((a, b) => a.sortOrder - b.sortOrder)

    // Calcular média global do período usando os avg_secs
    const totalAvgSeconds = timelineData.reduce((sum, entry) => sum + entry.avg_secs, 0)
    const globalAverage = totalAvgSeconds / timelineData.length

    return { hourlyData, globalAverage }
  }, [timelineData, selectedSeparator])



  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'Public Sans, sans-serif',
      selection: {
        enabled: !!onBarClick && !selectedSeparator
      },
      events: {
        dataPointSelection: function(event, chartContext, config) {
          if (onBarClick && !selectedSeparator) { // Só permite click na visão global
            const dataPointIndex = config.dataPointIndex
            const timelineEntry = timelineData[dataPointIndex]

            if (timelineEntry) {
              onBarClick({
                hour: hourlyData[dataPointIndex]?.hour || '',
                users: timelineEntry.users,
                avg_secs: timelineEntry.avg_secs,
                total_items: timelineEntry.total_items,
                total_secs: timelineEntry.total_secs
              })
            }
          }
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: hourlyData.length > 20 ? '95%' : hourlyData.length > 10 ? '80%' : '60%',
        borderRadius: 4,
        dataLabels: {
          position: 'top',
        },
      },
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.15,
        }
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'darken',
          value: 0.15,
        }
      }
    },
    dataLabels: {
      enabled: hourlyData.length <= 15,
      formatter: (val: number) => `${val.toFixed(0)}s`,
      offsetY: -20,
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: ['#111827'],
      },
    },
    xaxis: {
      categories: hourlyData.map(data => data.hour),
      labels: {
        rotate: hourlyData.length > 8 ? -45 : hourlyData.length > 5 ? -30 : 0,
        style: { fontSize: '11px' },
        maxHeight: hourlyData.length > 8 ? 80 : 60,
        trim: false,
        hideOverlappingLabels: false,
      },
    },
    yaxis: {
      min: 0,
      max: Math.max(...hourlyData.map(d => d.avgSeconds)) + 30,
      decimalsInFloat: 0,
      title: {
        text: 'Tempo Médio (segundos)',
        style: { fontSize: '12px', fontWeight: 600 },
      },
    },
    colors: ['#667eea'],
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 4,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val.toFixed(0)} segundos`,
      },
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const data = hourlyData[dataPointIndex]
        if (!data) return ''

        // Encontrar dados do horário na timeline original
        const timelineEntry = timelineData[dataPointIndex]

        if (selectedSeparator) {
          // Tooltip para separador individual
          return `
            <div style="padding: 12px; min-width: 200px;">
              <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #1976d2;">
                ${data.hour}h - ${selectedSeparator.split('.').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')}
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Tempo médio:</strong> ${data.avgSeconds.toFixed(0)}s
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Itens processados:</strong> ${data.totalItems.toLocaleString()}
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Produtividade:</strong> ${data.totalItems > 0 ? (data.totalItems / (data.totalSecs / 3600)).toFixed(1) : '0'} itens/h
              </div>
            </div>
          `
        } else {
          // Tooltip para visão global com detalhes dos separadores
          const topSeparators = timelineEntry?.users
            ?.sort((a: any, b: any) => a.avg_secs - b.avg_secs)
            ?.slice(0, 3) || []

          const worstSeparators = timelineEntry?.users
            ?.sort((a: any, b: any) => b.avg_secs - a.avg_secs)
            ?.slice(0, 2) || []

          return `
            <div style="padding: 12px; min-width: 250px;">
              <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #1976d2;">
                ${data.hour}h - Visão Geral
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Tempo médio global:</strong> ${data.avgSeconds.toFixed(0)}s
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Itens processados:</strong> ${data.totalItems.toLocaleString()}
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Separadores ativos:</strong> ${data.activeUsers}
              </div>
              <div style="margin-bottom: 8px;">
                <strong>Produtividade:</strong> ${(data.totalItems / (data.totalSecs / 3600)).toFixed(1)} itens/h
              </div>

              ${topSeparators.length > 0 ? `
                <div style="border-top: 1px solid #eee; padding-top: 8px; margin-top: 8px;">
                  <div style="font-weight: 600; font-size: 12px; margin-bottom: 4px; color: #4CAF50;">
                    🏆 Melhores Performances:
                  </div>
                  ${topSeparators.map((sep: any, idx: number) => `
                    <div style="font-size: 11px; margin-bottom: 2px;">
                      ${idx + 1}. ${sep.username.split('.').map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')}: ${sep.avg_secs.toFixed(0)}s
                    </div>
                  `).join('')}
                </div>
              ` : ''}

              ${worstSeparators.length > 0 ? `
                <div style="border-top: 1px solid #eee; padding-top: 8px; margin-top: 8px;">
                  <div style="font-weight: 600; font-size: 12px; margin-bottom: 4px; color: #FF9800;">
                    ⚠️ Necessitam Atenção:
                  </div>
                  ${worstSeparators.map((sep: any, idx: number) => `
                    <div style="font-size: 11px; margin-bottom: 2px;">
                      ${sep.username.split('.').map((p: string) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')}: ${sep.avg_secs.toFixed(0)}s
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          `
        }
      }
    },
  }

  const series: ApexOptions['series'] = [
    {
      name: 'Tempo médio por horário',
      data: hourlyData.map(data => data.avgSeconds),
    },
  ]

  if (!timelineData || timelineData.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Dados insuficientes para análise por horário</p>
      </div>
    )
  }

  return (
    <div>
      {title && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '8px', fontWeight: 600 }}>
            {title}
          </h3>
        </div>
      )}
      {onBarClick && !selectedSeparator && (
        <p style={{
          margin: 0,
          marginBottom: '16px',
          fontSize: '13px',
          color: '#666',
          fontStyle: 'italic',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          💡 Clique nas barras para ver o ranking detalhado de cada horário
        </p>
      )}
      <div style={{
        paddingBottom: hourlyData.length > 8 ? '40px' : '20px',
        ...(onBarClick && !selectedSeparator && {
          cursor: 'pointer',
          borderRadius: '8px',
          transition: 'all 0.2s ease'
        })
      }}>
        <ReactApexChart options={options} series={series} type="bar" height={350} />
      </div>
    </div>
  )
}

export default HourlyAverageChart
